import { FC } from 'react';

import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ExclamationIcon,
} from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/solid';
import {
  DELIVERY_CHANNEL_CONFIG,
  DeliveryChannel,
  GiftCouponByCode,
  GiftCouponRedeemStatus,
  NotificationType,
} from '@shared/api/giftCoupon/giftCoupon.interface';

import { ContactEmailLink } from '../../components/ContactEmailLink';
import CouponProductCard from './CouponProductCard';
import {
  formatKoreanDate,
  getNextCouponSendSlot,
  RedeemStatusMessage,
} from './giftCouponRedeem.utils';
import SendScheduleCard from './SendScheduleCard';

interface CouponStatusViewProps {
  coupon: GiftCouponByCode;
  statusMessage: RedeemStatusMessage;
  imageUrl: string;
  itemName: string;
  couponCode: string;
  brandName: string | null;
  validPeriod?: string;
}

const CouponStatusView: FC<CouponStatusViewProps> = ({
  coupon,
  statusMessage,
  imageUrl,
  itemName,
  couponCode,
  brandName,
  validPeriod,
}) => {
  const selectedUnit =
    coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object'
      ? coupon.selectedChoiceUnit
      : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white px-4 py-8">
      <div className="mx-auto max-w-md">
        {/* 상태 메시지 */}
        <div
          className={`rounded-xl p-6 text-center ${
            statusMessage.type === 'success'
              ? 'border border-stone-200 bg-stone-50'
              : statusMessage.type === 'info'
              ? 'border border-stone-200 bg-stone-50'
              : 'border border-red-100 bg-red-50'
          }`}
        >
          {statusMessage.type === 'success' ? (
            <CheckCircleSolidIcon className="mx-auto h-10 w-10 text-stone-600" />
          ) : statusMessage.type === 'info' ? (
            <CheckCircleIcon className="mx-auto h-10 w-10 text-stone-500" />
          ) : (
            <ExclamationCircleIcon className="mx-auto h-10 w-10 text-red-400" />
          )}
          <h2 className="mt-3 text-lg font-medium text-stone-800">
            {statusMessage.title}
          </h2>
          {statusMessage.message && (
            <p className="mt-1 whitespace-pre-line text-sm text-stone-500">
              {statusMessage.message}
            </p>
          )}
        </div>

        {/* Delivered: 알림톡/문자 안내 */}
        {coupon.redeemStatus === GiftCouponRedeemStatus.Delivered &&
          (() => {
            const channel = coupon.gifticon?.deliveryChannel;
            const config =
              channel && channel in DeliveryChannel
                ? DELIVERY_CHANNEL_CONFIG[channel as DeliveryChannel]
                : null;
            const isAlimTalk =
              !config || config.notificationType === NotificationType.AlimTalk;
            return (
              <div className="mt-4 overflow-hidden rounded-xl border border-stone-200 bg-white text-center shadow-sm">
                <div className="px-5 py-4">
                  {isAlimTalk ? (
                    <>
                      <p className="text-base font-bold text-stone-800">
                        알림톡(카카오톡)을 확인해 주세요
                      </p>
                      <p className="mt-1.5 text-xs text-stone-600">
                        카카오톡으로 발송된 알림톡을 확인해 주세요.
                        <br />
                        <span className="break-keep pt-1 font-bold">
                          카카오톡을 사용하지 않으시는 경우,
                          <br />
                          기프티콘 발송이 되지 않을 수 있습니다. <br />
                          하단 문의 메일로 말씀주세요.
                        </span>
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-stone-700">
                        받으신 문자 메시지를 확인해 주세요
                      </p>
                      {config && (
                        <div className="mt-3 inline-flex items-center gap-2.5 rounded-lg border border-stone-200 bg-stone-50 px-5 py-2.5">
                          <span className="text-xs font-medium text-stone-400">
                            발신번호
                          </span>
                          <span className="text-base font-bold text-stone-800">
                            {config.label}
                          </span>
                        </div>
                      )}
                      <p className="mt-2.5 break-keep text-xs text-stone-400">
                        &quot;{config?.label}&quot; 번호로 발송된 문자 메시지를 확인해
                        주세요.
                      </p>
                    </>
                  )}
                </div>
                {coupon.gifticon?.deliveredAt && (
                  <div className="border-t border-stone-100 bg-stone-50 px-5 py-2.5">
                    <p className="text-sm font-semibold text-stone-700">
                      {formatKoreanDate(new Date(coupon.gifticon.deliveredAt))} 발송
                    </p>
                  </div>
                )}
              </div>
            );
          })()}

        {/* Requested: 발송 예정 안내 */}
        {coupon.redeemStatus === GiftCouponRedeemStatus.Requested &&
          coupon.recipient?.requestedAt &&
          (() => {
            const reqAt = new Date(coupon.recipient.requestedAt);
            const sendAt = getNextCouponSendSlot(reqAt);
            return <SendScheduleCard requestedAt={reqAt} expectedSendAt={sendAt} />;
          })()}

        {/* Requested/Delivered 공통: 유효기간 + 수령방법 안내 */}
        {(coupon.redeemStatus === GiftCouponRedeemStatus.Requested ||
          coupon.redeemStatus === GiftCouponRedeemStatus.Delivered) && (
          <div className="mt-4 space-y-3">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-2.5">
                <ClockIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                <div>
                  <p className="text-sm font-bold text-amber-900">
                    기프티콘 유효기간 안내
                  </p>
                  <p className="mt-1.5 break-keep text-sm leading-relaxed text-amber-800">
                    발송된 기프티콘은 발송일로부터{' '}
                    <span className="font-bold">{validPeriod || '31일'}</span> 내에
                    사용하셔야 합니다.
                  </p>
                  <p className="mt-1 text-xs text-amber-700">
                    기한 내 미사용 시 소멸되며, 복구가 불가합니다.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <div className="flex items-start gap-2.5">
                <ExclamationIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                <div>
                  <p className="text-sm font-bold text-red-800">
                    카카오톡 &quot;선물함&quot;이 아닙니다
                  </p>
                  <p className="mt-1.5 break-keep text-sm leading-relaxed text-red-700">
                    기프티콘은{' '}
                    <span className="font-bold">문자(SMS) 또는 카카오톡 알림톡</span>
                    으로 발송됩니다.
                  </p>
                  <p className="mt-1 break-keep text-xs leading-relaxed text-red-600">
                    카카오톡 선물함이 아닌, 받으신 문자 또는 알림톡에서 기프티콘 이미지를
                    확인하여 매장에서 제시해 주세요.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 상품 정보 */}
        <CouponProductCard
          imageUrl={imageUrl}
          itemName={itemName}
          couponCode={couponCode}
          brandName={brandName}
          selectedUnit={selectedUnit}
        />

        {!statusMessage.hideContact && <ContactEmailLink />}
      </div>
    </div>
  );
};

export default CouponStatusView;
